import { RotateCcw } from 'lucide-react';
import clsx from 'clsx';
import type { SettingDefinitionDto } from '../../api';
import { DurationInput } from './DurationInput';

interface SettingItemProps {
  definition: SettingDefinitionDto;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  onReset: (key: string) => void;
  isModified: boolean;
  highlight?: boolean;
}

export function SettingItem({
  definition,
  value,
  onChange,
  onReset,
  isModified,
  highlight,
}: SettingItemProps) {
  const { key, label, description, type, defaultValue, constraints } = definition;

  const handleChange = (newValue: unknown) => {
    onChange(key, newValue);
  };

  const formatDefault = () => {
    if (type === 'checkbox') return defaultValue ? 'Attivo' : 'Disattivo';
    if (type === 'select') {
      const opt = constraints?.options?.find(o => o.value === String(defaultValue));
      return opt?.label ?? String(defaultValue);
    }
    return String(defaultValue);
  };

  return (
    <div
      id={`setting-${key}`}
      className={clsx(
        'grid grid-cols-1 md:grid-cols-[30%_1fr] gap-x-6 gap-y-1 py-4',
        'border-b border-gray-100 last:border-b-0',
        highlight && 'bg-yellow-50 -mx-4 px-4 rounded-lg'
      )}
    >
      {/* Left column: label + technical key */}
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {isModified && (
              <span className="w-2 h-2 rounded-full bg-primary-500 flex-shrink-0" title="Modificata" />
            )}
            <label className="text-sm font-medium text-gray-900">{label}</label>
          </div>
          <span className="text-xs text-gray-400 font-mono block mt-0.5">{key}</span>
        </div>
      </div>

      {/* Right column: input + default + description */}
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          {renderInput(type, value, constraints, handleChange)}
          {isModified && (
            <button
              type="button"
              onClick={() => onReset(key)}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
              title="Ripristina default"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
        </div>

        <p className="text-xs text-gray-400">
          Default: {formatDefault()}
        </p>

        <p className="text-sm text-gray-500">{description}</p>
      </div>
    </div>
  );
}

function renderInput(
  type: SettingDefinitionDto['type'],
  value: unknown,
  constraints: SettingDefinitionDto['constraints'],
  onChange: (value: unknown) => void
) {
  switch (type) {
    case 'number':
      return (
        <input
          type="number"
          value={value as number}
          onChange={e => onChange(parseFloat(e.target.value))}
          min={constraints?.min}
          max={constraints?.max}
          className="input w-32"
        />
      );

    case 'text':
    case 'password':
      return (
        <input
          type={type === 'password' ? 'password' : 'text'}
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          maxLength={constraints?.maxLength}
          className="input flex-1 max-w-md"
        />
      );

    case 'textarea':
      return (
        <textarea
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          maxLength={constraints?.maxLength}
          rows={3}
          className="input flex-1 max-w-md resize-y"
        />
      );

    case 'select':
      return (
        <select
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          className="input w-48"
        >
          {constraints?.options?.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      );

    case 'checkbox':
      return (
        <button
          type="button"
          role="switch"
          aria-checked={Boolean(value)}
          onClick={() => onChange(!value)}
          className={clsx(
            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
            value ? 'bg-primary-600' : 'bg-gray-200'
          )}
        >
          <span
            className={clsx(
              'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
              value ? 'translate-x-5' : 'translate-x-0'
            )}
          />
        </button>
      );

    case 'duration':
      return (
        <DurationInput
          value={String(value ?? '')}
          onChange={v => onChange(v)}
          min={constraints?.min}
          max={constraints?.max}
        />
      );

    default:
      return (
        <input
          type="text"
          value={String(value ?? '')}
          onChange={e => onChange(e.target.value)}
          className="input flex-1 max-w-md"
        />
      );
  }
}
